<template>
  <div id="app">
    <router-view />
    <!-- Custom Toast -->
    <div v-if="toastMessage" :class="['custom-toast', toastType]">
      {{ toastMessage }}
    </div>

    <Teleport to="body">
      <div
        v-if="showSubscriptionModal"
        class="subscription-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
        aria-describedby="subscription-modal-desc"
        @click.self="closeSubscriptionModal"
      >
        <div class="subscription-modal-card">
          <button
            type="button"
            class="subscription-modal-close"
            aria-label="Close dialog"
            @click="closeSubscriptionModal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <div class="subscription-modal-accent" aria-hidden="true" />
          <div class="subscription-modal-header">
            <div class="subscription-modal-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div class="subscription-modal-heading">
              <p class="subscription-modal-eyebrow">Billing & access</p>
              <h2 id="subscription-modal-title" class="subscription-modal-title">
                {{ isSubUser ? 'Account subscription required' : 'Choose your plan' }}
              </h2>
            </div>
          </div>
          <p id="subscription-modal-desc" class="subscription-modal-text">
            {{ isSubUser
              ? 'Plan expired. Contact admin to reactivate this account.'
              : 'Your account does not have an active subscription. Select a plan below to continue.' }}
          </p>

          <div v-if="isSubUser" class="subuser-modal-note">
            Please ask your admin to renew or upgrade the plan.
          </div>

          <div v-if="plansLoading" class="subscription-state">Loading plans...</div>
          <div v-else-if="plansError" class="subscription-state subscription-state-error">{{ plansError }}</div>
          <div
            v-else-if="!isSubUser && subscriptionPlans.length"
            class="subscription-plans-grid"
            :class="{ 'subscription-plans-grid-single': subscriptionPlans.length === 1 }"
          >
            <article v-for="plan in subscriptionPlans" :key="plan.planId" class="subscription-plan-card">
              <div class="subscription-plan-head">
                <h3>{{ plan.planname || 'Plan' }}</h3>
              </div>
              <p class="subscription-plan-desc">{{ plan.description || 'Plan details' }}</p>
              <div class="subscription-plan-price">{{ formatMoney(plan.amount, plan.currency) }}</div>
              <div class="subscription-plan-meta">
                <span>Per property / {{ formatInterval(plan.planinterval).replace('Per ', '').toLowerCase() }}</span>
                <span>Pay only for the properties you subscribe</span>
              </div>
              <ul class="subscription-feature-list subscription-feature-list-in-card">
                <li v-for="feature in includedFeatures" :key="`${plan.planId}-${feature}`">{{ feature }}</li>
              </ul>
              <button
                type="button"
                class="subscription-modal-btn subscription-modal-btn-primary subscription-plan-subscribe"
                @click="subscribeToPlan(plan)"
              >
                Subscribe
              </button>
            </article>
          </div>

          <div class="subscription-modal-actions">
            <button type="button" class="subscription-modal-btn subscription-modal-btn-secondary" @click="closeSubscriptionModal">
              {{ isSubUser ? 'Got it' : 'Not now' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div v-if="showPropertyCountPrompt" class="subscription-modal-overlay" @click.self="closePropertyCountPrompt">
        <div class="subscription-modal-card gateway-picker-card">
          <div class="subscription-modal-header">
            <div class="subscription-modal-heading">
              <p class="subscription-modal-eyebrow">Subscription</p>
              <h2 class="subscription-modal-title">How many properties?</h2>
            </div>
          </div>
          <div class="gateway-picker-body">
            <label class="property-count-label">
              Property count
              <input
                v-model.number="pendingPropertyCount"
                type="number"
                min="1"
                step="1"
                class="property-count-input"
                placeholder="e.g. 3"
              />
            </label>
            <p v-if="propertyCountError" class="subscription-state subscription-state-error">{{ propertyCountError }}</p>
          </div>
          <div class="subscription-modal-actions">
            <button type="button" class="subscription-modal-btn subscription-modal-btn-secondary" @click="closePropertyCountPrompt">
              Cancel
            </button>
            <button type="button" class="subscription-modal-btn subscription-modal-btn-primary" @click="confirmPropertyCount">
              Continue
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div v-if="showGatewayPicker" class="subscription-modal-overlay" @click.self="closeGatewayPicker">
        <div class="subscription-modal-card gateway-picker-card">
          <div class="subscription-modal-header">
            <div class="subscription-modal-heading">
              <p class="subscription-modal-eyebrow">Payment</p>
              <h2 class="subscription-modal-title">Choose payment gateway</h2>
            </div>
          </div>
          <div class="gateway-picker-body">
            <div v-if="gatewayLoading" class="subscription-loader-card subscription-state">
              <div class="subscription-loader-spinner" aria-hidden="true" />
              <p>Loading available gateways...</p>
            </div>
            <div v-else-if="isOpeningGateway" class="subscription-loader-card subscription-state">
              <div class="subscription-loader-spinner" aria-hidden="true" />
              <p>Opening payment gateway securely...</p>
            </div>
            <p v-else-if="gatewayError" class="subscription-state subscription-state-error">{{ gatewayError }}</p>
            <div v-else class="gateway-picker-list">
              <button
                v-for="gateway in gateways"
                :key="gateway.idgatewaycfg"
                type="button"
                class="gateway-picker-btn"
                :disabled="isOpeningGateway"
                @click="selectGateway(gateway)"
              >
                <span class="gateway-picker-main">
                  <span class="gateway-logo-wrap">
                    <img
                      v-if="gatewayLogo(gateway.idgatewaycfg)"
                      :src="gatewayLogo(gateway.idgatewaycfg)"
                      :alt="`${gateway.idgatewaycfg} logo`"
                      class="gateway-logo"
                    />
                  </span>
                  <span class="gateway-meta">
                    <strong>{{ gateway.idgatewaycfg }}</strong>
                    <small>{{ gateway.idgatewaycfg === 'ESEWA' ? 'Available now' : 'Coming soon' }}</small>
                  </span>
                </span>
                <span class="gateway-chevron">›</span>
              </button>
            </div>
          </div>
          <div class="subscription-modal-actions">
            <button type="button" class="subscription-modal-btn subscription-modal-btn-secondary" :disabled="isOpeningGateway" @click="closeGatewayPicker">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { HMS_SUBSCRIPTION_INACTIVE_EVENT } from '@/utilities/errorHelper'
import apiService from '@/services/api'
import { startEsewaSubscriptionCheckout } from '@/utilities/esewaPayment'
import esewaLogo from '@/assets/esewa.png'
import khaltiLogo from '@/assets/khalti.png'

export default {
  name: 'App',
  setup() {
    const isOnline = ref(navigator.onLine)
    const toastMessage = ref('')
    const toastType = ref('') // 'success' or 'error'
    let toastTimeout = null

    const showSubscriptionModal = ref(false)
    const subscriptionPlans = ref([])
    const showGatewayPicker = ref(false)
    const showPropertyCountPrompt = ref(false)
    const gatewayLoading = ref(false)
    const gateways = ref([])
    const gatewayError = ref('')
    const pendingPlan = ref(null)
    const pendingPropertyCount = ref(1)
    const propertyCountError = ref('')
    const isOpeningGateway = ref(false)
    const plansLoading = ref(false)
    const plansError = ref('')
    const includedFeatures = [
      'Dashboard',
      'Rooms',
      'Guests',
      'New Bookings',
      'Rent Payment',
      'Maintenance',
      'Outing Attendance',
      'Media Uploads',
      'Food and Menu',
      'Staff Management',
    ]
    const subscribeUrl = ref(
      typeof process !== 'undefined' && process.env?.VUE_APP_SUBSCRIBE_URL
        ? String(process.env.VUE_APP_SUBSCRIBE_URL).trim()
        : ''
    )
    const isSubUser = ref(false)
    const gatewayLogoMap = {
      ESEWA: esewaLogo,
      KHALTI: khaltiLogo,
    }

    const updateUserType = () => {
      try {
        const stored = localStorage.getItem('ADMIN_PROFILE_DATA')
        const parsed = stored ? JSON.parse(stored) : null
        isSubUser.value = String(parsed?.userType || '').toUpperCase() === 'SUBUSER'
      } catch {
        isSubUser.value = false
      }
    }

    const formatMoney = (amount, currency = 'NPR') => {
      const num = Number(amount)
      if (Number.isNaN(num)) return '—'
      const major = num / 100
      try {
        return new Intl.NumberFormat('en-NP', {
          style: 'currency',
          currency: currency || 'NPR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(major)
      } catch {
        return `${currency || 'NPR'} ${major.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
      }
    }

    const formatInterval = (interval) => {
      const i = String(interval || '').toUpperCase()
      if (i === 'YEAR' || i === 'YEARLY') return 'Per year'
      if (i === 'MONTH' || i === 'MONTHLY') return 'Per month'
      if (i === 'DAY' || i === 'DAILY') return 'Per day'
      return i ? i.toLowerCase().replace(/_/g, ' ') : 'Billing interval'
    }

    const fetchVisiblePlans = async () => {
      plansLoading.value = true
      plansError.value = ''
      try {
        const response = await apiService.getVisiblePlans()
        const list = response?.data?.data
        subscriptionPlans.value = Array.isArray(list) ? list : []
        if (!subscriptionPlans.value.length) {
          plansError.value = 'No plans are available right now.'
        }
      } catch (e) {
        console.error('Failed to fetch visible plans:', e)
        plansError.value = e?.response?.data?.reason || 'Failed to load plans.'
      } finally {
        plansLoading.value = false
      }
    }

    const openGatewayPicker = async (plan, propertyCount) => {
      pendingPlan.value = plan
      pendingPropertyCount.value = Number(propertyCount) || 1
      showGatewayPicker.value = true
      gatewayLoading.value = true
      gatewayError.value = ''
      gateways.value = []
      try {
        const response = await apiService.getPaymentGateways()
        const list = Array.isArray(response?.data) ? response.data : []
        gateways.value = list.filter((g) => String(g?.gatewaystatus || '').toUpperCase() === 'ACTIVE')
        if (!gateways.value.length) {
          gatewayError.value = 'No active payment gateway available right now.'
        }
      } catch (e) {
        console.error('Failed to fetch gateways:', e)
        gatewayError.value = e?.response?.data?.reason || 'Failed to load payment gateways.'
      } finally {
        gatewayLoading.value = false
      }
    }

    const closeGatewayPicker = () => {
      if (isOpeningGateway.value) {
        return
      }
      showGatewayPicker.value = false
      gatewayLoading.value = false
      gatewayError.value = ''
      gateways.value = []
      pendingPlan.value = null
      pendingPropertyCount.value = 1
      propertyCountError.value = ''
    }

    const promptPropertyCount = (plan) => {
      pendingPlan.value = plan
      pendingPropertyCount.value = 1
      propertyCountError.value = ''
      showPropertyCountPrompt.value = true
    }

    const closePropertyCountPrompt = () => {
      showPropertyCountPrompt.value = false
      propertyCountError.value = ''
      pendingPropertyCount.value = 1
      pendingPlan.value = null
    }

    const confirmPropertyCount = () => {
      const count = Number(pendingPropertyCount.value)
      if (!Number.isInteger(count) || count <= 0) {
        propertyCountError.value = 'Please enter a valid property count.'
        return
      }
      const plan = pendingPlan.value
      showPropertyCountPrompt.value = false
      propertyCountError.value = ''
      openGatewayPicker(plan, count)
    }

    const selectGateway = (gateway) => {
      const code = String(gateway?.idgatewaycfg || '').toUpperCase()
      if (code !== 'ESEWA') {
        gatewayError.value = `${code || 'This'} gateway is coming soon.`
        return
      }
      isOpeningGateway.value = true
      startEsewaSubscriptionCheckout(pendingPlan.value, pendingPropertyCount.value).catch((e) => {
        console.error('Failed to initiate eSewa payment:', e)
        gatewayError.value = e?.response?.data?.reason || e?.message || 'Could not start payment.'
        isOpeningGateway.value = false
      })
    }

    const subscribeToPlan = (plan) => {
      promptPropertyCount(plan)
    }

    const gatewayLogo = (code) => {
      const key = String(code || '').toUpperCase()
      return gatewayLogoMap[key] || ''
    }

    const closeSubscriptionModal = () => {
      showSubscriptionModal.value = false
    }

    const onHmsSubscriptionInactive = () => {
      if (showSubscriptionModal.value) {
        return
      }
      updateUserType()
      showSubscriptionModal.value = true
      if (!isSubUser.value) {
        fetchVisiblePlans()
      } else {
        subscriptionPlans.value = []
        plansError.value = ''
        plansLoading.value = false
      }
    }

    const showToast = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      if (toastTimeout) clearTimeout(toastTimeout)
      toastTimeout = setTimeout(() => {
        toastMessage.value = ''
        toastType.value = ''
      }, 3000)
    }

    const updateOnlineStatus = () => {
      const nowOnline = navigator.onLine
      if (isOnline.value !== nowOnline) {
        isOnline.value = nowOnline
        if (nowOnline) {
          showToast('✅ Back online', 'success')
        } else {
          showToast('⚠ No internet connection', 'error')
        }
      }
    }

    const onGlobalToast = (event) => {
      const detail = event?.detail || {}
      const message = typeof detail.message === 'string' ? detail.message : ''
      const type = detail.type === 'success' ? 'success' : 'error'
      if (!message) {
        return
      }
      showToast(message, type)
    }

    onMounted(() => {
      window.addEventListener('online', updateOnlineStatus)
      window.addEventListener('offline', updateOnlineStatus)
      window.addEventListener(HMS_SUBSCRIPTION_INACTIVE_EVENT, onHmsSubscriptionInactive)
      window.addEventListener('nexthome:toast', onGlobalToast)

      // Optional: show a message if initially offline
      if (!isOnline.value) {
        showToast('⚠ You are currently offline', 'error')
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('online', updateOnlineStatus)
      window.removeEventListener('offline', updateOnlineStatus)
      window.removeEventListener(HMS_SUBSCRIPTION_INACTIVE_EVENT, onHmsSubscriptionInactive)
      window.removeEventListener('nexthome:toast', onGlobalToast)
    })

    return {
      toastMessage,
      toastType,
      showSubscriptionModal,
      isSubUser,
      subscriptionPlans,
      showGatewayPicker,
      showPropertyCountPrompt,
      gatewayLoading,
      gateways,
      gatewayError,
      isOpeningGateway,
      plansLoading,
      plansError,
      pendingPropertyCount,
      propertyCountError,
      includedFeatures,
      subscribeUrl,
      formatMoney,
      formatInterval,
      gatewayLogo,
      subscribeToPlan,
      closePropertyCountPrompt,
      confirmPropertyCount,
      closeGatewayPicker,
      selectGateway,
      closeSubscriptionModal,
    }
  },
}
</script>

<style>
* {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#app {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

/* Custom toast styles */
.custom-toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  animation: fadeInOut 3s forwards;
}

.custom-toast.success {
  background-color: #28a745;
}

.custom-toast.error {
  background-color: #dc3545;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  10%,
  90% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
}

.subscription-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  animation: subscriptionFadeIn 0.2s ease;
}

.subscription-modal-card {
  position: relative;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  background: linear-gradient(180deg, #faf8ff 0%, #ffffff 45%, #ffffff 100%);
  border-radius: 16px;
  padding: 0;
  box-shadow:
    0 0 0 1px rgba(91, 46, 142, 0.1),
    0 4px 6px -1px rgba(91, 46, 142, 0.06),
    0 20px 50px -12px rgba(45, 24, 72, 0.2);
  text-align: left;
  overflow: hidden;
  overflow-y: auto;
  animation: subscriptionSlideUp 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.subscription-modal-accent {
  height: 3px;
  width: 100%;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
}

.subscription-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.subscription-modal-close:hover {
  background: rgba(91, 46, 142, 0.08);
  color: #5b2e8e;
}

.subscription-modal-close:focus-visible {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

.subscription-modal-header {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.75rem 1.75rem 0 1.75rem;
  padding-right: 3.25rem;
}

.subscription-modal-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #5b2e8e;
  box-shadow: 0 1px 2px rgba(91, 46, 142, 0.08);
  border: 1px solid rgba(91, 46, 142, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.subscription-modal-heading {
  min-width: 0;
  padding-top: 2px;
}

.subscription-modal-eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5b2e8e;
}

.subscription-modal-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.025em;
  line-height: 1.25;
}

.subscription-modal-text {
  margin: 0;
  padding: 1rem 1.75rem 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #475569;
  font-weight: 400;
}

.subscription-modal-reason-wrap {
  margin: 1.25rem 1.75rem 0;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #faf5ff 0%, #ffffff 100%);
  border: 1px solid rgba(91, 46, 142, 0.14);
  box-shadow: 0 1px 2px rgba(91, 46, 142, 0.05);
}

.subscription-modal-reason-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7c4dbd;
  margin-bottom: 0.35rem;
}

.subscription-modal-reason {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #334155;
}

.subscription-modal-divider {
  height: 1px;
  margin: 1.5rem 1.75rem 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(91, 46, 142, 0.12) 15%,
    rgba(91, 46, 142, 0.12) 85%,
    transparent 100%
  );
}

.subscription-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  justify-content: flex-end;
  margin-top: 1.1rem;
  padding: 1rem 1.75rem 1.6rem;
  border-top: 1px solid rgba(91, 46, 142, 0.12);
  background: rgba(255, 255, 255, 0.95);
}

.subscription-feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.subscription-feature-list li {
  font-size: 0.72rem;
  font-weight: 600;
  color: #5b2e8e;
  padding: 0.28rem 0.5rem;
  border-radius: 999px;
  background: rgba(91, 46, 142, 0.08);
  border: 1px solid rgba(91, 46, 142, 0.15);
}

.subscription-state {
  margin: 1rem 1.75rem 0;
  color: #475569;
  font-size: 0.9rem;
}

.subscription-loader-card {
  margin: 0;
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.14);
  background: linear-gradient(150deg, #ffffff 0%, #f7f4ff 100%);
  box-shadow: 0 8px 18px rgba(91, 46, 142, 0.08);
  padding: 0.9rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.subscription-loader-card p {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #4b5563;
}

.subscription-loader-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2.5px solid rgba(91, 46, 142, 0.16);
  border-top-color: #5b2e8e;
  border-right-color: #7c4dbd;
  animation: subscriptionSpin 0.85s linear infinite;
  flex-shrink: 0;
}

@keyframes subscriptionSpin {
  to {
    transform: rotate(360deg);
  }
}

.subscription-state-error {
  color: #b91c1c;
}

.subuser-modal-note {
  margin: 0.85rem 1.75rem 0;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: linear-gradient(145deg, #fffbeb 0%, #fff 100%);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #92400e;
  font-size: 0.86rem;
  line-height: 1.5;
}

.gateway-picker-card {
  max-width: 460px;
}

.gateway-picker-body {
  padding: 1rem 1.75rem 0.5rem;
}

.gateway-picker-list {
  display: grid;
  gap: 0.85rem;
}

.property-count-label {
  display: grid;
  gap: 0.45rem;
  font-size: 0.86rem;
  color: #475569;
  font-weight: 600;
}

.property-count-input {
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  padding: 0 0.75rem;
  font-size: 0.92rem;
}

.gateway-picker-btn {
  width: 100%;
  border: 1px solid rgba(91, 46, 142, 0.2);
  background: #fff;
  border-radius: 12px;
  padding: 0.88rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #1e293b;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}

.gateway-picker-btn:hover {
  border-color: rgba(91, 46, 142, 0.36);
  box-shadow: 0 6px 16px rgba(91, 46, 142, 0.12);
  transform: translateY(-1px);
}

.gateway-picker-main {
  display: inline-flex;
  align-items: center;
  gap: 0.9rem;
}

.gateway-logo-wrap {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.24);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.gateway-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.gateway-meta {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.gateway-meta strong {
  font-size: 0.98rem;
  font-weight: 700;
  color: #111827;
}

.gateway-picker-btn small {
  font-size: 0.76rem;
  color: #64748b;
  font-weight: 600;
}

.gateway-chevron {
  color: #64748b;
  font-size: 1.25rem;
  font-weight: 700;
}

.subscription-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.875rem;
  margin: 1rem 1.75rem 1.1rem;
  padding-bottom: 1.35rem;
}

.subscription-plans-grid-single {
  grid-template-columns: minmax(300px, 560px);
  justify-content: center;
}

.subscription-plan-card {
  border-radius: 12px;
  border: 1px solid rgba(91, 46, 142, 0.16);
  background: linear-gradient(160deg, #ffffff 0%, #faf8fc 100%);
  padding: 0.875rem;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.08);
  display: flex;
  flex-direction: column;
  text-align: left;
  min-height: 100%;
  min-width: 0;
}

.subscription-plan-head {
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  align-items: flex-start;
}

.subscription-plan-head h3 {
  margin: 0;
  font-size: 1rem;
  color: #1e293b;
}

.subscription-plan-desc {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
  min-height: 2.3em;
}

.subscription-plan-price {
  margin-top: 0.65rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: #5b2e8e;
}

.subscription-plan-meta {
  margin-top: 0.55rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.65rem;
}

.subscription-plan-meta span {
  font-size: 0.68rem;
  font-weight: 600;
  color: #334155;
  background: #f3f4f6;
  padding: 0.26rem 0.46rem;
  border-radius: 999px;
}

@media (max-width: 700px) {
  .subscription-plans-grid-single {
    grid-template-columns: 1fr;
  }
}

.subscription-plan-subscribe {
  width: 100%;
  margin-top: auto;
}

.subscription-feature-list-in-card {
  border-top: 1px solid rgba(91, 46, 142, 0.12);
  padding-top: 0.6rem;
  margin-top: 0.2rem;
}

.subscription-modal-btn {
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.625rem 1.125rem;
  min-height: 40px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}

.subscription-modal-btn:active {
  transform: translateY(1px);
}

.subscription-modal-btn-primary {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #fff;
  box-shadow: 0 1px 2px rgba(91, 46, 142, 0.15);
}

.subscription-modal-btn-primary:hover {
  background: linear-gradient(135deg, #4d2677 0%, #6b3da8 100%);
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.35);
}

.subscription-modal-btn-secondary {
  background: #fff;
  color: #475569;
  border: 1px solid rgba(91, 46, 142, 0.2);
  box-shadow: 0 1px 2px rgba(91, 46, 142, 0.04);
}

.subscription-modal-btn-secondary:hover {
  background: #faf8ff;
  border-color: #5b2e8e;
  color: #5b2e8e;
}

@keyframes subscriptionFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes subscriptionSlideUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
